import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { invalidateShipmentLists } from '#/entities/shipment'
import { useResolveCustomer } from '#/features/order-create'
import { buildShipmentUpdate } from '#/widgets/shipment-card'
import { buildPaymentUpdate } from '#/widgets/payment-card'
import {
  getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey,
  useUpdateShipmentApiV1ShipmentsShipmentIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'
import type { ShipmentUpdate } from '#/shared/api/generated/shipment/models'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import type { OrderFormPayload } from '../ui/order-form'

interface UseUpdateShipmentParams {
  shipmentId: string
  /** The shipment's current customer — enables persisting customer-card edits. */
  customer?: { id?: string; email?: string }
  onSuccess?: () => void
}

function buildUpdateBody(payload: OrderFormPayload): ShipmentUpdate {
  const shipmentBody = buildShipmentUpdate({
    vehicles: payload.vehicles,
    location: payload.location,
    shipmentFields: payload.shipment,
  })
  const paymentBody = buildPaymentUpdate(payload.payment)
  const body: ShipmentUpdate = { ...shipmentBody, ...paymentBody }
  if (payload.customerId) body.customer_id = payload.customerId
  return body
}

export function useUpdateShipment({
  shipmentId,
  customer,
  onSuccess,
}: UseUpdateShipmentParams) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useUpdateShipmentApiV1ShipmentsShipmentIdPatch()
  const resolveCustomer = useResolveCustomer()

  const submit = async (payload: OrderFormPayload): Promise<boolean> => {
    const toastId = toast.loading(t('orderEdit.toast.saving'))
    try {
      const body = buildUpdateBody(payload)
      if (payload.customerDirty && customer) {
        const resolvedId = await resolveCustomer.resolve({
          form: payload.customer,
          customerId: payload.customerId ?? customer.id,
          originalEmail: customer.email,
        })
        if (resolvedId !== customer.id) body.customer_id = resolvedId
      }
      await mutation.mutateAsync({
        shipmentId,
        data: body,
      })
      queryClient.invalidateQueries({
        queryKey: getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(shipmentId),
      })
      invalidateShipmentLists(queryClient)
      toast.success(t('orderEdit.toast.success'), { id: toastId })
      onSuccess?.()
      return true
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t('orderEdit.toast.error'), {
        id: toastId,
      })
      return false
    }
  }

  return {
    submit,
    isSubmitting: mutation.isPending || resolveCustomer.isResolving,
  }
}
